import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { tones, type ToneName } from '@/theme';

/**
 * Tarjeta de indicador (KPI) del sistema de diseño.
 *
 * Es la cifra grande de la referencia visual: etiqueta en gris tenue, número en 28px con cifras
 * tabulares (para que dos tarjetas vecinas no bailen) y un pie de una línea que explica el número.
 *
 * Dos reglas de los reportes que este componente hace cumplir por construcción:
 *  - el VALOR llega ya formateado como texto (`'25.000'`, `'—'`, `'12,5 %'`): la pantalla decide cómo
 *    se pinta cada cosa y acá no se inventa ningún formato. Un `null` del backend se muestra «—»
 *    porque el dueño del dato es quien sabe si falta («no se pudo calcular») o es cero de verdad;
 *  - `loading` pinta un bloque gris del MISMO alto que el número, así el layout no salta cuando llega
 *    la respuesta (el panel carga cuatro o cinco KPIs a la vez).
 */

export interface KpiCardProps {
  label: string;
  /** Valor YA formateado (`formatNumber`, `formatPorcentaje`, `'—'`…). */
  value: string;
  /** Sufijo pegado al número, en cuerpo pequeño («/ 12 confirmadas», «msj/conv.»). */
  unit?: string;
  /** Una línea debajo del número: qué cuenta ese indicador. */
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: ToneName;
  /** Aviso del pie, para lo que hay que mirar con lupa (ej. «3 canceladas»). */
  footnote?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function KpiCard({
  label,
  value,
  unit,
  hint,
  icon,
  tone = 'brand',
  footnote,
  loading = false,
  className,
}: KpiCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-card border border-line bg-surface p-4 shadow-card sm:p-5',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-caption font-medium text-ink-muted">{label}</p>

        {icon ? (
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-pill border',
              tones[tone].className,
            )}
            aria-hidden="true"
          >
            {icon}
          </span>
        ) : null}
      </div>

      {loading ? (
        <span className="block h-9 w-28 animate-pulse rounded-control bg-surface-muted" />
      ) : (
        <p className="flex flex-wrap items-baseline gap-x-1.5">
          <span className={cn('tabular text-kpi font-semibold', tones[tone].text)}>{value}</span>
          {unit ? <span className="text-caption text-ink-muted">{unit}</span> : null}
        </p>
      )}

      {hint ? <p className="text-micro text-ink-faint">{hint}</p> : null}

      {footnote ? <div className="mt-auto text-micro text-ink-muted">{footnote}</div> : null}
    </div>
  );
}
